import { useEffect, useRef, useState } from "react";
import type { ReactNode } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, RotateCw } from "lucide-react";
import { ApiError } from "@gwrctl/api-client";
import { client } from "../lib/client.js";
import { humanizeError } from "../lib/errors.ts";
import { useEventListener } from "../lib/events.tsx";
import { jobTitle, paramSummary } from "../lib/labels.ts";
import { useToast } from "../lib/toast.tsx";
import { jobDuration, stepSummary } from "../lib/release.ts";
import { PageBody, PageHeader } from "../components/Shell.tsx";
import {
  Banner,
  Button,
  Card,
  ConfirmButton,
  Dot,
  Empty,
  ErrorState,
  StatusPill,
} from "../components/Ui.tsx";
import { LogView } from "../components/LogView.tsx";
import { JobLauncher } from "../components/JobLauncher.tsx";

const ACTIVE = new Set(["queued", "claimed", "running"]);
const RETRYABLE = new Set(["failed", "canceled", "expired"]);

interface Step {
  seq: number;
  text: string;
  at: string;
  bad: boolean;
}

export function JobDetailPage(): ReactNode {
  const { jobId = "" } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const toast = useToast();
  const [steps, setSteps] = useState<Step[]>([]);
  const [lineCount, setLineCount] = useState(0);
  const [retrying, setRetrying] = useState(false);
  const [canceling, setCanceling] = useState(false);
  const seenSeq = useRef(new Set<number>());

  const { data, error, isLoading, refetch } = useQuery({
    queryKey: ["job", jobId],
    queryFn: () => client.job(jobId),
    enabled: Boolean(jobId),
    // Olay akışı koparsa da durum güncel kalsın; bitince yoklama durur.
    refetchInterval: (q) => (q.state.data && !ACTIVE.has(q.state.data.job.status) ? false : 3000),
    // Silinmiş kayıt için üç kez denemek yalnızca "Yükleniyor…"u uzatır.
    retry: (count, err) => !(err instanceof ApiError && err.status === 404) && count < 2,
  });

  const job = data?.job;
  const canRetry = Boolean(job?.appId && RETRYABLE.has(job.status));

  // Tekrar deneme formu uygulamanın iş özetini (onay seviyesi, uyarı) ve kanallarını ister.
  const appQuery = useQuery({
    queryKey: ["app", job?.appId],
    queryFn: () => client.app(job!.appId!),
    enabled: canRetry,
  });
  const retrySummary = appQuery.data?.jobs.find((j) => j.id === job?.type);

  // Tekrar deneme yeni işin sayfasına gider; form orada yeniden açılmasın.
  useEffect(() => setRetrying(false), [jobId]);

  // Adımlar ayrı bir alandan değil, agent'ın "system" akışındaki satırlardan
  // çıkarılıyor — iş ne yaptıysa adım listesi odur, ayrıca modellemeye gerek yok.
  useEffect(() => {
    if (!jobId) return;
    setSteps([]);
    setLineCount(0);
    seenSeq.current = new Set();
    let cancelled = false;
    client
      .jobLogs(jobId)
      .then((logs) => {
        if (cancelled) return;
        setLineCount(logs.length);
        const collected: Step[] = [];
        for (const log of logs) {
          seenSeq.current.add(log.seq);
          if (log.stream === "system" || log.stream === "stderr") {
            collected.push({ seq: log.seq, text: log.chunk, at: log.ts, bad: log.stream === "stderr" });
          }
        }
        setSteps(collected.slice(-40));
      })
      .catch(() => {
        // Log okunamazsa adım listesi boş kalır; iş kaydı ayrı sorguda hatasını gösteriyor.
      });
    return () => {
      cancelled = true;
    };
  }, [jobId]);

  useEventListener(
    (event) => {
      if (event.t === "job.log" && event.jobId === jobId) {
        if (seenSeq.current.has(event.seq)) return;
        seenSeq.current.add(event.seq);
        setLineCount((n) => n + 1);
        if (event.stream === "system" || event.stream === "stderr") {
          setSteps((prev) =>
            [
              ...prev,
              { seq: event.seq, text: event.chunk, at: new Date().toISOString(), bad: event.stream === "stderr" },
            ].slice(-40),
          );
        }
      }
      if ((event.t === "job.update" || event.t === "process.status") && event.jobId === jobId) {
        void queryClient.invalidateQueries({ queryKey: ["job", jobId] });
      }
    },
    [jobId],
  );

  async function cancel(): Promise<void> {
    setCanceling(true);
    try {
      await client.cancelJob(jobId);
      await queryClient.invalidateQueries({ queryKey: ["job", jobId] });
    } catch (err) {
      toast({ tone: "danger", title: "İptal edilemedi", body: humanizeError(err) });
    } finally {
      setCanceling(false);
    }
  }

  if (!job) {
    return (
      <>
        <PageHeader
          kicker="iş"
          title={error ? "İş açılamadı" : "…"}
          actions={
            <Button icon={ArrowLeft} onClick={() => navigate("/jobs")}>
              Tüm işler
            </Button>
          }
        />
        <PageBody>
          <Card flush>
            {error ? (
              <ErrorState error={error} onRetry={() => void refetch()} />
            ) : (
              <Empty>{isLoading ? "Yükleniyor…" : "İş bulunamadı"}</Empty>
            )}
          </Card>
        </PageBody>
      </>
    );
  }

  const active = ACTIVE.has(job.status);
  const params = paramSummary(job.params);
  const summary = stepSummary(job);
  const duration = jobDuration(job);

  return (
    <>
      <PageHeader
        kicker={job.appId ?? "sistem"}
        title={jobTitle(job.type)}
        meta={
          <span className="mono text-2xs text-neutral-600">
            {job.type} · {job.id}
          </span>
        }
        actions={
          <>
            <Button
              icon={ArrowLeft}
              onClick={() => navigate(job.appId ? `/apps/${job.appId}` : "/jobs")}
            >
              {job.appId ? "Uygulamaya dön" : "Tüm işler"}
            </Button>
            {canRetry && (
              <Button
                variant="primary"
                icon={RotateCw}
                loading={appQuery.isLoading}
                disabled={!retrySummary}
                title={
                  appQuery.error
                    ? humanizeError(appQuery.error)
                    : appQuery.data && !retrySummary
                      ? "Bu iş artık bu uygulama için tanımlı değil"
                      : undefined
                }
                onClick={() => setRetrying(true)}
              >
                Tekrar dene
              </Button>
            )}
            {active && (
              <ConfirmButton loading={canceling} onConfirm={cancel} confirmLabel="İptal edilsin mi?">
                İptal et
              </ConfirmButton>
            )}
          </>
        }
      />

      <PageBody>
        <div className="card flex flex-wrap items-center gap-x-4 gap-y-2.5 p-4">
          <StatusPill status={job.status} />
          {job.appId && <span className="tag tag-neutral">{job.appId}</span>}
          <span className="min-w-0 text-2xs break-words text-neutral-500">{params || "parametresiz"}</span>
          {active && (
            <span role="status" className="inline-flex items-center gap-2 text-xs text-warn">
              <span className="pulse inline-block size-1.5 rounded-full bg-warn" aria-hidden />
              {job.step ?? "çalışıyor"}
            </span>
          )}
          <span className="mono ml-auto text-2xs text-neutral-600">
            {new Date(job.createdAt).toLocaleString("tr-TR")}
            {duration ? ` · ${duration}` : ""}
          </span>
        </div>

        {appQuery.error && canRetry && (
          <Card flush>
            <ErrorState error={appQuery.error} onRetry={() => void appQuery.refetch()} compact />
          </Card>
        )}

        {job.processUrl && (
          <Card title="Test sunucusu" hint="yalnızca agent'ın makinesinde açılır">
            <a
              href={job.processUrl}
              target="_blank"
              rel="noreferrer"
              className="text-sm break-all text-accent-300 underline underline-offset-4"
            >
              {job.processUrl}
            </a>
          </Card>
        )}

        {job.error && <Banner tone="danger">{humanizeError(job.error)}</Banner>}

        <div className="grid items-start gap-4 lg:grid-cols-[260px_minmax(0,1fr)]">
          <Card title="Adımlar" flush>
            {steps.length ? (
              steps.map((step) => (
                <div key={step.seq} className="row items-start gap-2.5 py-2.5">
                  <span className="mt-1.5">
                    <Dot tone={step.bad ? "danger" : "ok"} />
                  </span>
                  <div className="grid min-w-0 gap-0.5">
                    <span className="text-xs break-words text-pretty text-neutral-200">{step.text}</span>
                    <span className="mono text-2xs text-neutral-600">
                      {new Date(step.at).toLocaleTimeString("tr-TR")}
                    </span>
                  </div>
                </div>
              ))
            ) : (
              <Empty>Adım yok</Empty>
            )}
          </Card>

          <Card
            title="Canlı log"
            hint="stdout · ws akışı"
            action={<span className="mono text-2xs text-neutral-600">{lineCount} satır</span>}
            flush
            className="min-w-0 overflow-hidden"
          >
            <LogView jobId={jobId} active={active} />
          </Card>
        </div>

        {job.result != null && (
          <Card title="Sonuç" flush>
            <p className="px-4 pt-3.5 pb-2 text-sm text-neutral-200">
              {summary ?? "İş bir sonuç döndürdü; ayrıntısı aşağıda."}
            </p>
            <details className="border-t border-neutral-900">
              <summary className="cursor-pointer px-4 py-2.5 text-2xs text-neutral-500 hover:text-neutral-300">
                Ham sonuç (JSON)
              </summary>
              <pre className="log-surface m-0 max-h-[320px] overflow-auto p-4 text-2xs">
                {JSON.stringify(job.result, null, 2)}
              </pre>
            </details>
          </Card>
        )}
      </PageBody>

      {retrying && retrySummary && job.appId && appQuery.data && (
        <JobLauncher
          job={retrySummary}
          appId={job.appId}
          appName={appQuery.data.app.displayName}
          channels={appQuery.data.app.ota?.channels ?? ["production"]}
          buildScript={appQuery.data.app.build?.script}
          initialParams={job.params}
          onClose={() => setRetrying(false)}
        />
      )}
    </>
  );
}
